import { useEffect, useState, useCallback } from "react";
import ReactTooltip from "react-tooltip";
import FadeComponent from "../../../components/FadeComponent";
import ArrowDownBlueIcon from "../../../components/svgs/ArrowDownBlueIcon";
import { POINT_FIELDS, userRoles } from "../../../utils/constants";
import { getCurrentLevel } from "../../../utils/getCurrentLevel";
import { getBadgeImageFromUrl } from "../../../utils/imageService";
import PositionStatusInfo from "../PositionStatusInfo";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import withRouter from "../../../HOC/withRouter";
import { compose } from "react-recompose";
import IconMoreInfo from "../../../components/svgs/MoreInfo";
import "./styles.scss";

const isFieldDone = (field, profile, quiz, survey, touchPoints) => {
  switch (field) {
    case "profilePicturesPoints":
      return !!profile.photoURL;
    case "profileDetailsPoints":
      return !!(profile.userRole || profile.roleDescription);
    case "favoriteBrandPoints":
      return !!(
        profile.favoriteBrand ||
        profile.favoriteBrandImage ||
        profile.favoriteChildBrand ||
        profile.favoriteChildBrandImage
      );
    case "correctQuizPoints":
    case "quizPoints":
      return !(
        (quiz && Array.isArray(quiz.unAnswered) && quiz.unAnswered.length > 0) ||
        (quiz && Array.isArray(quiz.hidden) && quiz.hidden.length > 0)
      );
    case "clientSurveyPoints":
      return !(
        (survey && Array.isArray(survey.unAnswered) && survey.unAnswered.length > 0) ||
        (survey && Array.isArray(survey.hidden) && survey.hidden.length > 0)
      );
    case "surveyPoints":
      const state = touchPoints?.filter(tp => tp.surveyState === "started");
      return !(state && state.length > 0);
    default:
      return false;
  }
};

const StatusPointsInfo = ({
  t,
  ranking,
  rankingStage,
  rankingPosition,
  distanceToNext,
  clientRanking,
  profile,
  quiz,
  survey,
  client,
  touchPoints,
  onlyBadges,
  questAnswer = null,
  questPointsAnimationVisible = false,
  wheelPoints = null,
  wheelPointsAnimationVisible = false,
  hideQuestAnimation = () => {},
}) => {
  useEffect(() => {
    setShowAnimatedPoints(questPointsAnimationVisible || wheelPointsAnimationVisible);
  }, [questPointsAnimationVisible, wheelPointsAnimationVisible]);

  const currentLevel = getCurrentLevel(ranking, clientRanking);
  const position = !rankingPosition || rankingPosition <= 3 || rankingPosition > 10 ? rankingPosition : 4;
  const positionBadge = rankingPosition && rankingPosition <= 10 && clientRanking[`position${position}Badge`];
  const pointsToNextLevel = clientRanking[`level${currentLevel + 1}Points`] - ranking;
  const levelPointsBarWidth = Math.floor((ranking / clientRanking[`level${currentLevel + 1}Points`]) * 100);
  const positionPointsBarWidth = Math.floor((ranking / (ranking + distanceToNext)) * 100);
  const ponintFields = Object.keys(clientRanking)
    .filter(
      field =>
        POINT_FIELDS.has(field) && !isFieldDone(field, profile, quiz, survey, touchPoints) && clientRanking[field] > 0,
    )
    .sort((a, b) => clientRanking[a] - clientRanking[b]);
  const countType = ranking === 1 ? "singular" : "plural";

  const [showPositionProgress, setShowPositionProgress] = useState(false);
  const [showAnimatedPoints, setShowAnimatedPoints] = useState(false);
  const statusPointsText = client?.ranking?.lblStatuspoints;

  const earnedPoints = wheelPointsAnimationVisible
    ? wheelPoints
    : questPointsAnimationVisible
      ? questAnswer?.questPoints
      : "";
  const animationContent = showAnimatedPoints && earnedPoints && `+${earnedPoints} ${statusPointsText}`;

  const getToolTip = () => {
    return (
      <span className="progress-additional-info">
        <div className="info-icon" data-tip data-for="progress">
          <IconMoreInfo color={client?.primaryTextColor} />
        </div>
        <ReactTooltip
          id="progress"
          className="tooltip"
          effect="solid"
          type="light"
          place="bottom"
          border={false}
          multiline={true}
        >
          {ponintFields.map((pointField, i) => (
            <div className="point-field" key={i}>
              <div className="red-font">+ {clientRanking[`${pointField}`]} </div>
              <div className="red-font">
                {clientRanking[`${pointField}`] > 1
                  ? t("statusPoints.pointPlural", { lblStatuspoints: client?.ranking?.lblStatuspoints })
                  : t("statusPoints.pointSingular", { lblStatuspoint: client?.ranking?.lblStatuspoint })}{" "}
              </div>
              <div className="regular-font">
                {t(`statusPoints.${pointField}`, { lblTouchPoint: client?.lblTouchPoint })}
              </div>
            </div>
          ))}
        </ReactTooltip>
      </span>
    );
  };

  const Badges = useCallback(
    () => (
      <div className={showPositionProgress ? "badges-container column" : "badges-container"}>
        {currentLevel > 0 && (
          <div className="status-badge-container">
            <div className="img-container">
              <img src={getBadgeImageFromUrl(rankingStage.badge, currentLevel, "level")} alt="" />
            </div>
            <span>{clientRanking[`level${currentLevel}Text`]}</span>
          </div>
        )}
        {clientRanking.showRanking && position && position <= 4 && (
          <div className="status-badge-container position-badge">
            <div className="img-container" style={{ display: "flex", justifyContent: "center" }}>
              <img src={getBadgeImageFromUrl(positionBadge, position, "position")} alt="" style={{ maxHeight: 70 }} />
            </div>
            <span>{clientRanking[`position${position}Text`]}</span>
          </div>
        )}
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLevel, position, positionBadge, rankingStage.badge, showPositionProgress],
  );

  if (onlyBadges) {
    return <Badges />;
  }

  return (
    <div className={"status-points-info"}>
      <FadeComponent show={showAnimatedPoints} onEndAction={hideQuestAnimation} content={animationContent} />
      <div className="level-info">
        <Badges />
        {currentLevel < 3 ? (
          <div className={currentLevel > 0 ? "status-points-progress" : "status-points-progress no-badge"}>
            {getToolTip()}
            <div className="status-progress-text">
              {t(`statusPoints.StatusPointsCount.${countType}`, {
                count: ranking,
                lblStatuspoint: client?.ranking?.lblStatuspoint,
                lblStatuspoints: client?.ranking?.lblStatuspoints,
              })}
            </div>
            <div className="status-progress">
              <div className="status-progress-bar">
                <div className="status-progress-bar current-points" style={{ width: `${levelPointsBarWidth}%` }}></div>
              </div>
            </div>
            <span className="status-sub-text">
              {t("statusPoints.pointsToNextLevel", {
                points: pointsToNextLevel,
                nextLevel: clientRanking[`level${currentLevel + 1}Text`],
                lblStatuspoints: client?.ranking?.lblStatuspoints,
              })}
            </span>
          </div>
        ) : (
          <div className={currentLevel > 0 ? "status-points-progress" : "status-points-progress no-badge"}>
            {getToolTip()}
            <div className="status-progress-text">
              {t(`statusPoints.StatusPointsCount.${countType}`, {
                count: ranking,
                lblStatuspoint: client?.ranking?.lblStatuspoint,
                lblStatuspoints: client?.ranking?.lblStatuspoints,
              })}
            </div>
            <div className="status-progress">
              <div className="status-progress-bar">
                <div className="status-progress-bar current-points" style={{ width: `${levelPointsBarWidth}%` }}></div>
              </div>
            </div>
          </div>
        )}
      </div>
      {clientRanking.showRanking && showPositionProgress && position && position <= 4 && (
        <PositionStatusInfo
          t={t}
          client={client}
          clientRanking={clientRanking}
          positionBadge={positionBadge}
          position={position}
          positionPointsBarWidth={positionPointsBarWidth}
          distanceToNext={distanceToNext}
        />
      )}
      {clientRanking.showRanking && position && position <= 4 && (
        <span
          onClick={() => setShowPositionProgress(!showPositionProgress)}
          className={`arrow-icon ${showPositionProgress && "rotate"}`}
        >
          <ArrowDownBlueIcon color={client?.altColor} />
        </span>
      )}
      {clientRanking.showRanking && showPositionProgress && position && position >= 11 && currentLevel > 0 && (
        <PositionStatusInfo
          t={t}
          client={client}
          clientRanking={clientRanking}
          positionBadge={positionBadge}
          position={position}
          positionPointsBarWidth={positionPointsBarWidth}
          distanceToNext={distanceToNext}
        />
      )}
      {clientRanking.showRanking && position && position >= 11 && currentLevel > 0 && (
        <span
          onClick={() => setShowPositionProgress(!showPositionProgress)}
          className={`arrow-icon ${showPositionProgress && "rotate"}`}
        >
          <ArrowDownBlueIcon color={client?.altColor} />
        </span>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  profile: state.user.profile,
  client: state.client.client,
  quiz: state.quiz.quiz,
  survey: state.survey.survey,
  touchPoints: state.touchPoint?.touchPoints || [],
});

export default compose(
  withRouter,
  connect(mapStateToProps),
)(
  withTranslation()(p => {
    const profile = p.customProfile || p.profile;
    const canRender =
      p.client &&
      p.client.ranking &&
      p.client.ranking.isActive &&
      profile &&
      profile.ranking > -1 &&
      profile.rankingStage &&
      profile.role !== userRoles.auditor;

    if (!canRender) return null;

    return (
      <StatusPointsInfo
        {...p}
        ranking={profile.ranking}
        rankingStage={profile.rankingStage}
        rankingPosition={profile.rankingPosition}
        distanceToNext={profile.distanceToNext}
        clientRanking={p.client.ranking}
        profile={profile}
        quiz={p.quiz}
        survey={p.survey}
        client={p.client}
        touchPoints={p.touchPoints}
      />
    );
  }),
);
